// frontend/src/pages/CustomerList.jsx
import React, { useEffect, useMemo, useState } from "react";

export default function CustomerList() {
  const [customers, setCustomers] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");
  const [err, setErr] = useState("");
  const [okMsg, setOkMsg] = useState("");

  // delete confirm modal
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setErr(""); setOkMsg("");
    fetch("/api/customers")
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data) => { if (alive) setCustomers(Array.isArray(data) ? data : []); })
      .catch((e) => { if (alive) setErr(String(e.message || e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const sortedCustomers = useMemo(() => {
    return [...customers].sort((a, b) => {
      const as = (a?.surname || "").toLowerCase();
      const bs = (b?.surname || "").toLowerCase();
      const cmp = as.localeCompare(bs);
      if (cmp !== 0) return cmp;
      const af = (a?.first_name || "").toLowerCase();
      const bf = (b?.first_name || "").toLowerCase();
      return af.localeCompare(bf);
    });
  }, [customers]);

  const filteredCustomers = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sortedCustomers;
    return sortedCustomers.filter((c) => {
      const parts = [c?.surname, c?.first_name, c?.town, c?.email, c?.nie]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return parts.includes(q);
    });
  }, [sortedCustomers, query]);

  useEffect(() => {
    if (!selectedId) return setFormData({});
    const cust = customers.find((c) => String(c.id) === String(selectedId));
    if (cust) setFormData({ ...cust });
  }, [selectedId, customers]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!selectedId) return;
    setSaving(true);
    setErr(""); setOkMsg("");
    try {
      const res = await fetch(`/api/customers/${selectedId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (!res.ok) throw new Error((await res.text().catch(() => "")) || `HTTP ${res.status}`);
      setCustomers((prev) =>
        prev.map((c) => (String(c.id) === String(selectedId) ? { ...c, ...formData } : c))
      );
      setOkMsg("Customer saved.");
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setSaving(false);
    }
  };

  const requestDelete = () => {
    if (!selectedId) return;
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    const id = selectedId;
    if (!id) return;
    setSaving(true);
    setErr(""); setOkMsg("");
    try {
      const res = await fetch(`/api/customers/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error((await res.text().catch(() => "")) || `HTTP ${res.status}`);
      setCustomers((prev) => prev.filter((c) => String(c.id) !== String(id)));
      setSelectedId("");
      setFormData({});
      setOkMsg("Customer deleted.");
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setSaving(false);
      setConfirmOpen(false);
    }
  };

  const handleClear = () => {
    setSelectedId("");
    setFormData({});
    setErr(""); setOkMsg("");
  };

  const disabled = !selectedId || saving;

  return (
    <div className="cl-fullbleed">
      <div className="card">
        {/* Dark theme + full-bleed */}
        <style>{`
  /* Dark theme tokens */
  :root[data-theme="dark"] {
    --bg: #0b1220;
    --card: #121a2b;
    --text: #e8eef7;
    --muted: #8ea0b8;
    --border: #22314a;
    --btn: #2d9cdb;
    --btn-text: #fff;
    --btn-secondary: #fff;
    --btn-secondary-text: #3c3c42;
    --table-row: #101828;
    --table-row-alt: #0f172a;
    --danger: #ef4444;
    --danger-text: #fff;
  }

  /* Full-bleed wrapper (mobile-safe) */
  .cl-fullbleed {
    padding: 16px clamp(12px, 2vw, 24px);
    background: var(--bg);
    width: 100%;
    max-width: 100%;
    color: var(--text);
  }
  @media (min-width: 720px) {
    .cl-fullbleed {
      position: relative;
      left: 50%;
      right: 50%;
      margin-left: calc(50% - 50vw);
      margin-right: calc(50% - 50vw);
      width: 100vw;
      max-width: 100vw;
    }
  }

  /* Card */
  .card {
    background: var(--card);
    color: var(--text);
    border: 1px solid var(--border);
    border-radius: 16px;
    box-shadow: none;
  }
  .cl-header {
    padding: 10px 12px;
    border-bottom: 1px solid var(--border);
    font-weight: 700;
  }
  .cl-body { padding: 12px; }
  .muted { color: var(--muted); }

  /* Layout */
  .cl-row { display: flex; align-items: center; gap: .75rem; margin-bottom: .75rem; flex-wrap: wrap; }
  .cl-selectwrap { display: flex; align-items: center; gap: .5rem; }

  /* Inputs / selects / textareas */
  .cl-select,
  .cl-field .input,
  .cl-body input,
  .cl-body select,
  .cl-body textarea {
    width: 100%;
    padding: .55rem .7rem;
    border-radius: .55rem;
    background: var(--table-row);
    color: var(--text);
    border: 1px solid var(--border);
    outline: none;
  }
  .cl-select { min-width: 320px; }
  @media (max-width: 420px) { .cl-select { min-width: 100%; } }

  .cl-field label {
    display: block;
    margin-bottom: 6px;
    font-size: 12px;
    color: var(--muted);
    font-weight: 600;
  }

  /* Placeholders + focus */
  .cl-body input::placeholder,
  .cl-body textarea::placeholder { color: var(--muted); opacity: .95; }
  .cl-body input:focus,
  .cl-body select:focus,
  .cl-body textarea:focus {
    border-color: var(--btn);
    box-shadow: 0 0 0 2px color-mix(in srgb, var(--btn) 25%, transparent);
  }

  /* Buttons */
  .btn {
    appearance: none;
    padding: .55rem .85rem;
    border-radius: .55rem;
    font-weight: 700;
    cursor: pointer;
    border: 1px solid var(--border);
    background: var(--table-row);
    color: var(--text);
  }
  .btn.primary {
    background: var(--btn);
    color: var(--btn-text);
    border-color: var(--btn);
  }
  .btn.secondary {
    background: var(--btn-secondary);
    color: var(--btn-secondary-text);
    border-color: var(--btn-secondary);
  }
  .btn.danger {
    background: var(--danger);
    color: var(--danger-text);
    border-color: var(--danger);
  }
  .btn[disabled] { opacity: .65; cursor: not-allowed; }

  /* Action row spacing */
  .cl-actions {
    display: flex; gap: 8px; flex-wrap: wrap; margin-top: 12px;
  }

  /* Tables (if present) */
  .cl-table { width: 100%; border-collapse: collapse; }
  .cl-table th, .cl-table td { padding: .6rem .75rem; border: 1px solid var(--border); }
  .cl-table thead th { background: var(--card); color: var(--text); }
  .cl-table tbody tr:nth-child(odd)  { background: var(--table-row); }
  .cl-table tbody tr:nth-child(even) { background: var(--table-row-alt); }
        `}</style>

        <div className="cl-header">Customer Editor</div>

        <div className="cl-body" style={{ paddingBottom: 0 }}>
          {err && (
            <div style={{
              marginBottom: 12, padding: "10px 12px", borderRadius: 8,
              border: "1px solid #fecaca", background: "#fee2e2", color: "#991b1b",
            }}>
              {err}
            </div>
          )}
          {okMsg && (
            <div style={{
              marginBottom: 12, padding: "10px 12px", borderRadius: 8,
              border: "1px solid #bbf7d0", background: "#dcfce7", color: "#166534",
            }}>
              {okMsg}
            </div>
          )}

          {/* Filter */}
          <div className="cl-row">
            <label htmlFor="customer-filter" className="label">Filter</label>
            <input
              id="customer-filter"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Type surname, first name, town, email or NIE"
              className="input"
              aria-label="Filter customers"
              style={{ maxWidth: 460 }}
            />
          </div>

          {/* Selector */}
          <div className="cl-row">
            <label htmlFor="customer-select" className="label">Choose customer</label>
            <div className="cl-selectwrap">
              <select
                id="customer-select"
                value={selectedId}
                onChange={(e) => setSelectedId(e.target.value)}
                className="cl-select"
                aria-label="Select a customer by surname"
                size={8}
              >
                <option value="" disabled>
                  {loading ? "Loading customers…" : `— Select a customer (${filteredCustomers.length}) —`}
                </option>
                {filteredCustomers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {(c.surname || "").trim()}, {(c.first_name || "").trim()}
                    {c.town ? ` — ${c.town}` : ""}
                  </option>
                ))}
              </select>
              {selectedId && (
                <button type="button" className="btn" onClick={handleClear}>
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Form grid */}
        <div className="cl-body">
          <div className="cl-grid">
            <div className="cl-field">
              <label>First name</label>
              <input name="first_name" value={formData.first_name || ""} onChange={handleChange} className="input" disabled={!selectedId} />
            </div>
            <div className="cl-field">
              <label>Surname</label>
              <input name="surname" value={formData.surname || ""} onChange={handleChange} className="input" disabled={!selectedId} />
            </div>

            <div className="cl-field" style={{ gridColumn: "1 / -1" }}>
              <label>Address 1</label>
              <input name="address" value={formData.address || ""} onChange={handleChange} className="input" disabled={!selectedId} />
            </div>
            <div className="cl-field">
              <label>Address 2</label>
              <input name="address2" value={formData.address2 || ""} onChange={handleChange} className="input" disabled={!selectedId} />
            </div>
            <div className="cl-field">
              <label>Address 3</label>
              <input name="address3" value={formData.address3 || ""} onChange={handleChange} className="input" disabled={!selectedId} />
            </div>

            <div className="cl-field">
              <label>Post Code</label>
              <input name="postcode" value={formData.postcode || ""} onChange={handleChange} className="input" disabled={!selectedId} />
            </div>
            <div className="cl-field">
              <label>Town</label>
              <input name="town" value={formData.town || ""} onChange={handleChange} className="input" disabled={!selectedId} />
            </div>
            <div className="cl-field">
              <label>Province</label>
              <input name="province" value={formData.province || ""} onChange={handleChange} className="input" disabled={!selectedId} />
            </div>

            <div className="cl-field">
              <label>Tel</label>
              <input name="tel" value={formData.tel || ""} onChange={handleChange} className="input" disabled={!selectedId} />
            </div>
            <div className="cl-field">
              <label>Email</label>
              <input name="email" value={formData.email || ""} onChange={handleChange} className="input" disabled={!selectedId} />
            </div>
            <div className="cl-field">
              <label>NIE</label>
              <input name="nie" value={formData.nie || ""} onChange={handleChange} className="input" disabled={!selectedId} />
            </div>
          </div>

          {/* Actions */}
          <div className="cl-actions">
            <button type="button" onClick={handleSave} className="btn primary" disabled={disabled}>
              {saving ? "Saving…" : "Save"}
            </button>
            <button type="button" onClick={requestDelete} className="btn danger" disabled={disabled}>
              Delete
            </button>
            <button type="button" onClick={handleClear} className="btn secondary" disabled={!selectedId || saving}>
              Cancel
            </button>
          </div>

          <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
            Select a customer from the list to edit their details.
          </div>
        </div>
      </div>

      {/* Confirm Delete Modal */}
      {confirmOpen && (
        <div
          onClick={() => !saving && setConfirmOpen(false)}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,.45)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 16, zIndex: 1100
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            role="dialog" aria-modal="true" aria-labelledby="confirm-title"
            style={{
              width: "min(520px, 96vw)", background: "var(--card)", color: "var(--text)",
              border: "1px solid var(--border)", borderRadius: 12
            }}
          >
            <div id="confirm-title" style={{ padding: "14px 18px", borderBottom: "1px solid var(--border)", fontWeight: 700 }}>
              Confirm delete
            </div>
            <div style={{ padding: 18 }}>
              {selectedId ? (
                <div>
                  Delete customer <strong>ID {selectedId}</strong>? This cannot be undone.
                </div>
              ) : (
                <div>Delete this customer? This cannot be undone.</div>
              )}
            </div>
            <div style={{ padding: 12, borderTop: "1px solid var(--border)", display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="btn secondary" onClick={() => setConfirmOpen(false)} disabled={saving}>No</button>
              <button className="btn danger" onClick={confirmDelete} disabled={saving}>
                {saving ? "Deleting…" : "Yes, delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
